// src/components/layout/Footer.jsx
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import BookyaraLogo from "../ui/BookyaraLogo";
import { useTenant } from "../../context/TenantContext";
import { useNavigate, useParams } from "react-router-dom";
import { displayPhone } from "../../utils/phone";

export default function Footer() {
  const { tenant } = useTenant();
  const navigate   = useNavigate();
  const { slug }   = useParams();
  const base       = `/${slug}`;

  if (!tenant) return null;

  return (
    <footer className="bg-white border-t border-surface-200 mt-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              {tenant.logoUrl
                ? <img src={tenant.logoUrl} alt={tenant.name} className="w-8 h-8 object-cover rounded-lg shrink-0" />
                : <BookyaraLogo size="sm" variant="icon" />
              }
              <span className="font-semibold text-xl text-ink-900">{tenant.name}</span>
            </div>
            <p className="text-ink-400 text-sm leading-relaxed mb-4">
              {tenant.tagline}. Premium grooming, effortless scheduling.
            </p>
            {tenant.rating && (
              <div className="badge-brand">
                {tenant.rating}★ · {tenant.reviews}+ reviews
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-ink-700 font-semibold text-sm mb-4 uppercase tracking-widest">Contact</h4>
            <ul className="space-y-3">
              {[
                { icon: MapPin, text: tenant.address },
                { icon: Phone,  text: displayPhone(tenant.phone) },
                { icon: Mail,   text: tenant.email   },
                { icon: Clock,  text: tenant.hours   },
              ].filter(item => item.text).map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 text-sm text-ink-400">
                  <Icon size={15} className="text-brand-500 mt-0.5 shrink-0" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-ink-700 font-semibold text-sm mb-4 uppercase tracking-widest">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { label: "Home",             path: base },
                { label: "Services",         path: `${base}/services` },
                { label: "Book Appointment", path: `${base}/book` },
              ].map(({ label, path }) => (
                <li key={label}>
                  <button onClick={() => navigate(path)}
                    className="text-sm text-ink-400 hover:text-brand-600 transition-colors">
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-surface-100 mt-10 pt-6 flex flex-col sm:flex-row
                        items-center justify-between gap-3">
          <p className="text-ink-300 text-xs font-mono">
            © {new Date().getFullYear()} {tenant.name}. All rights reserved.
          </p>
          {tenant.plan !== "pro" && (
            <p className="text-ink-300 text-xs">Powered by Bookyara Platform</p>
          )}
        </div>
      </div>
    </footer>
  );
}
